import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
} from '@mui/material';
import type { CarBrandList, CarSubTypeList, CarTypeList } from 'entities/car';
import { useParams, useNavigate } from 'react-router-dom';

import { apiClient } from 'shared/api';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DynamicParams } from 'shared/ui/dynamic-params';
import { Loader } from 'shared/ui/loader';
import { SelectOrCreate } from 'shared/ui/select-or-create';
import { TextInputSmall } from 'shared/ui/text-input-small';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './catalog-item-admin.module.scss';

interface DynamicParam {
  id: string;
  fieldName: string;
  fieldValue: string[];
}

export const CatalogItemAdmin = () => {
  const { uid } = useParams();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState<any>({});
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [dynamicParams, setDynamicParams] = useState<DynamicParam[]>([]);

  // Определяем режим работы
  const isCreateMode = !uid || uid === 'new';

  // Списки для селектов
  const [carTypes, setCarTypes] = useState<CarTypeList>([]);
  const [carSubtypes, setCarSubtypes] = useState<CarSubTypeList>([]);
  const [carBrands, setCarBrands] = useState<CarBrandList>([]);

  // Новые состояния для режимов select/create
  const [typeMode, setTypeMode] = useState<'select' | 'create'>('select');
  const [subtypeMode, setSubtypeMode] = useState<'select' | 'create'>('select');
  const [brandMode, setBrandMode] = useState<'select' | 'create'>('select');

  // Новые значения для создания
  const [newTypeName, setNewTypeName] = useState('');
  const [newSubtypeName, setNewSubtypeName] = useState('');
  const [newBrandName, setNewBrandName] = useState('');

  // Валидация полей
  const validateFields = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Проверка названия модели
    if (!vehicle.name || vehicle.name.trim() === '') {
      errors.name = 'Название модели обязательно для заполнения';
    }

    // Проверка типа
    if (typeMode === 'select') {
      if (!vehicle.car_type?.id) {
        errors.car_type = 'Тип ТС обязателен для выбора';
      }
    } else {
      if (!newTypeName.trim()) {
        errors.car_type = 'Название нового типа обязательно для заполнения';
      }
    }

    // Проверка подтипа
    if (subtypeMode === 'select') {
      if (!vehicle.car_subtype?.id) {
        errors.car_subtype = 'Подтип ТС обязателен для выбора';
      }
    } else {
      if (!newSubtypeName.trim()) {
        errors.car_subtype = 'Название нового подтипа обязательно для заполнения';
      }
      if (typeMode === 'select' && !vehicle.car_type?.id) {
        errors.car_subtype = 'Для создания подтипа необходимо выбрать или создать тип';
      }
    }

    // Проверка бренда
    if (brandMode === 'select') {
      if (!vehicle.car_brand?.id) {
        errors.car_brand = 'Бренд обязателен для выбора';
      }
    } else {
      if (!newBrandName.trim()) {
        errors.car_brand = 'Название нового бренда обязательно для заполнения';
      }
    }

    // Проверка параметров (опционально - можно добавить валидацию если нужно)
    // const invalidParams = dynamicParams.some(param =>
    //   !param.fieldName.trim() || !param.fieldValue.trim()
    // );
    // if (invalidParams) {
    //   errors.params = 'Все параметры должны иметь название и значение';
    // }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Загрузка данных для селектов
  const loadCarTypes = async () => {
    try {
      const response = await apiClient.get('/car-types/all');
      setCarTypes(response.data.data || []);
    } catch (error) {
      throw new Error('Ошибка при загрузке типов');
    }
  };

  const loadCarSubtypes = async (typeId: number) => {
    try {
      const response = await apiClient.post('/car-subtypes/subtypes', { typeId });
      setCarSubtypes(response.data.data || []);
    } catch (error) {
      throw new Error('Ошибка при загрузке подтипов');
    }
  };

  const loadCarBrands = async (typeId?: number, subtypeId?: number) => {
    try {
      let response;
      if (subtypeId) {
        response = await apiClient.post('/car-brands/subtype', { subtypeId });
      } else if (typeId) {
        response = await apiClient.post('/car-brands/type', { typeId });
      } else {
        response = await apiClient.get('/car-brands/all');
      }
      setCarBrands(response.data.data || []);
    } catch (error) {
      throw new Error('Ошибка при загрузке брендов');
    }
  };

  // Функции для создания новых сущностей
  const createNewType = async (name: string, value: string = 'cars') => {
    try {
      const response = await apiClient.post('/car-types/create', { name, value });
      return response.data.data;
    } catch (error) {
      throw new Error('Ошибка при создании типа');
    }
  };

  const createNewSubtype = async (name: string, typeId: number) => {
    try {
      const response = await apiClient.post('/car-subtypes/create', { name, typeId });
      return response.data.data;
    } catch (error) {
      throw new Error('Ошибка при создании подтипа');
    }
  };

  const createNewBrand = async (name: string) => {
    try {
      const response = await apiClient.post('/car-brands/create', { name });
      return response.data.data;
    } catch (error) {
      throw new Error('Ошибка при создании бренда');
    }
  };

  // Функции для преобразования параметров
  const convertJsonToParams = (carParams: any): DynamicParam[] => {
    if (!carParams || typeof carParams !== 'object') {
      return [];
    }

    // Если carParams это массив объектов с fieldName и fieldValue
    if (Array.isArray(carParams)) {
      return carParams.map((param, index) => ({
        id: `param_${index}_${Date.now()}`,
        fieldName: param.fieldName || '',
        fieldValue: Array.isArray(param.fieldValue)
          ? param.fieldValue
          : [param.fieldValue].filter(Boolean),
      }));
    }

    // Если carParams это объект ключ-значение
    return Object.entries(carParams).map(([key, value], index) => ({
      id: `param_${index}_${Date.now()}`,
      fieldName: key,
      fieldValue: Array.isArray(value) ? value : [String(value || '')].filter(Boolean),
    }));
  };

  const convertParamsToJson = (params: DynamicParam[]): any[] => {
    return params
      .filter((param) => param.fieldName.trim() && param.fieldValue.length > 0)
      .map((param) => ({
        fieldName: param.fieldName.trim(),
        fieldValue: param.fieldValue.filter((val) => val.trim()),
      }));
  };

  const getVehicle = async () => {
    if (isCreateMode) {
      // В режиме создания инициализируем пустой объект
      setVehicle({
        name: '',
        car_params: [],
        car_type: null,
        car_subtype: null,
        car_brand: null,
      });
      setDynamicParams([]);
      setIsEditing(true); // Автоматически включаем режим редактирования

      // Загружаем базовые данные для селектов
      await loadCarTypes();
      await loadCarBrands(); // Загружаем все бренды
      return;
    }

    try {
      const response = await apiClient.get(`/vehicles/item/${uid}`);
      const data = response.data.data ?? {};

      setVehicle(data);

      // Преобразуем car_params в динамические параметры
      const params = convertJsonToParams(data.car_params);
      setDynamicParams(params);

      // Загружаем данные для селектов
      await loadCarTypes();
      if (data.car_type?.id) {
        await loadCarSubtypes(data.car_type.id);
        await loadCarBrands(data.car_type.id);
      }
    } catch (error) {
      throw new Error('Ошибка при загрузке данных');
    }
  };

  useEffect(() => {
    getVehicle();
  }, [uid]);

  const saveVehicle = async () => {
    try {
      // Валидируем все поля перед сохранением
      if (!validateFields()) {
        return; // Не сохраняем если есть ошибки валидации
      }

      let finalCarType = vehicle.car_type;
      let finalCarSubtype = vehicle.car_subtype;
      let finalCarBrand = vehicle.car_brand;

      // Создаем новые сущности если необходимо
      if (typeMode === 'create' && newTypeName.trim()) {
        finalCarType = await createNewType(newTypeName.trim());
      }

      if (subtypeMode === 'create' && newSubtypeName.trim() && finalCarType?.id) {
        finalCarSubtype = await createNewSubtype(newSubtypeName.trim(), finalCarType.id);
      }

      if (brandMode === 'create' && newBrandName.trim()) {
        finalCarBrand = await createNewBrand(newBrandName.trim());
      }

      // Преобразуем динамические параметры в JSON
      const carParamsJson = convertParamsToJson(dynamicParams);

      const vehicleData = {
        ...vehicle,
        car_type: finalCarType,
        car_subtype: finalCarSubtype,
        car_brand: finalCarBrand,
        car_params: carParamsJson,
      };

      if (isCreateMode) {
        // Создание новой записи
        await apiClient.post('/vehicles', {
          data: vehicleData,
        });
      } else {
        // Обновление существующей записи
        await apiClient.put(`/vehicles/${vehicle.id}`, {
          data: vehicleData,
        });
      }

      setShowConfirmDialog(false);
      setIsEditing(false);
      setFieldErrors({});

      if (isCreateMode) {
        // После создания перенаправляем на список
        navigate('/dashboard/admin/catalog');
      } else {
        // После обновления перезагружаем данные
        await getVehicle();
      }
    } catch (error) {
      throw new Error('Ошибка при сохранении');
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVehicle({ ...vehicle, name: e.target.value });
    // Сбрасываем ошибку названия
    setFieldErrors((prev) => ({
      ...prev,
      name: '',
    }));
  };

  const handleParamsChange = (params: DynamicParam[]) => {
    setDynamicParams(params);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setFieldErrors({}); // Сбрасываем ошибки при входе в режим редактирования
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFieldErrors({});
    getVehicle(); // Восстанавливаем исходные данные
  };

  const handleSave = () => {
    setFieldErrors({}); // Сбрасываем ошибки перед валидацией
    setShowConfirmDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const deleteVehicle = async () => {
    try {
      await apiClient.delete(`/vehicles/item/${vehicle.id}`);
      setShowDeleteDialog(false);
      navigate('/dashboard/admin/catalog');
    } catch (error) {
      throw new Error('Ошибка при удалении записи');
    }
  };

  return vehicle ? (
    <Box className={styles.agentsPage}>
      <DashboardHeaderBackLink
        link={'/dashboard/admin/catalog'}
        title={isCreateMode ? 'Создание новой записи ' : vehicle.name || '-'}
      />
      <Box>
        <Card className={styles.card}>
          <CardHeader
            title={isCreateMode ? 'Создание новой записи' : vehicle.name || '-'}
            action={
              !isEditing ? (
                <Box style={{ display: 'flex', gap: '10px' }}>
                  <ButtonCustom onClick={handleEdit} className={styles.greenBtn}>
                    Редактировать
                  </ButtonCustom>
                  {!isCreateMode && (
                    <ButtonCustom onClick={handleDelete} className={styles.redBtn}>
                      Удалить
                    </ButtonCustom>
                  )}
                </Box>
              ) : (
                <Box style={{ display: 'flex', gap: '10px' }}>
                  <ButtonCustom onClick={handleCancel} className={styles.modalButtonCancel}>
                    Отмена
                  </ButtonCustom>
                  <ButtonCustom onClick={handleSave} className={styles.greenBtn}>
                    {isCreateMode ? 'Создать' : 'Сохранить'}
                  </ButtonCustom>
                </Box>
              )
            }
          />
          <CardContent className={styles.cardContent}>
            <List>
              <ListItem className={styles.listItem}>
                <b>Модель:</b>
                {isEditing ? (
                  <Box style={{ width: '100%' }}>
                    <TextInputSmall
                      value={vehicle.name || ''}
                      onChange={handleNameChange}
                      placeholder='Название модели'
                      error={!!fieldErrors.name}
                    />
                    {fieldErrors.name && (
                      <Box style={{ color: '#FF3A3A', fontSize: '12px', marginTop: '5px' }}>
                        {fieldErrors.name}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <span>{vehicle?.name || '-'}</span>
                )}
              </ListItem>

              <ListItem className={styles.listItem}>
                {isEditing ? (
                  <Box style={{ width: '100%' }}>
                    <SelectOrCreate
                      label='Тип ТС'
                      value={vehicle.car_type}
                      newValue={newTypeName}
                      options={carTypes}
                      onSelectChange={(selected) => {
                        setVehicle({
                          ...vehicle,
                          car_type: selected,
                          car_subtype: null,
                          car_brand: null,
                        });
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_type: '',
                          car_subtype: '',
                          car_brand: '',
                        }));
                        if (selected?.id) {
                          loadCarSubtypes(selected.id);
                          loadCarBrands(selected.id);
                        }
                      }}
                      onNewValueChange={(newValue) => {
                        setNewTypeName(newValue);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_type: '',
                        }));
                      }}
                      placeholder='Выберите тип'
                      error={!!fieldErrors.car_type}
                      mode={typeMode}
                      onModeChange={(mode) => {
                        setTypeMode(mode);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_type: '',
                        }));
                      }}
                    />
                    {fieldErrors.car_type && (
                      <Box style={{ color: '#FF3A3A', fontSize: '12px', marginTop: '5px' }}>
                        {fieldErrors.car_type}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <span>{vehicle.car_type?.name || '-'}</span>
                )}
              </ListItem>

              <ListItem className={styles.listItem}>
                {isEditing ? (
                  <Box style={{ width: '100%' }}>
                    <SelectOrCreate
                      label='Подтип ТС'
                      value={vehicle.car_subtype}
                      newValue={newSubtypeName}
                      options={carSubtypes}
                      onSelectChange={(selected) => {
                        setVehicle({
                          ...vehicle,
                          car_subtype: selected,
                          car_brand: null,
                        });
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_subtype: '',
                          car_brand: '',
                        }));
                        if (selected?.id) {
                          loadCarBrands(vehicle.car_type?.id, selected.id);
                        }
                      }}
                      onNewValueChange={(newValue) => {
                        setNewSubtypeName(newValue);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_subtype: '',
                        }));
                      }}
                      placeholder='Выберите подтип'
                      error={!!fieldErrors.car_subtype}
                      mode={subtypeMode}
                      onModeChange={(mode) => {
                        setSubtypeMode(mode);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_subtype: '',
                        }));
                      }}
                    />
                    {fieldErrors.car_subtype && (
                      <Box style={{ color: '#FF3A3A', fontSize: '12px', marginTop: '5px' }}>
                        {fieldErrors.car_subtype}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <span>{vehicle.car_subtype?.name || '-'}</span>
                )}
              </ListItem>

              <ListItem className={styles.listItem}>
                {isEditing ? (
                  <Box style={{ width: '100%' }}>
                    <SelectOrCreate
                      label='Бренд ТС'
                      value={vehicle.car_brand}
                      newValue={newBrandName}
                      options={carBrands}
                      onSelectChange={(selected) => {
                        setVehicle({
                          ...vehicle,
                          car_brand: selected,
                        });
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_brand: '',
                        }));
                      }}
                      onNewValueChange={(newValue) => {
                        setNewBrandName(newValue);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_brand: '',
                        }));
                      }}
                      placeholder='Выберите бренд'
                      error={!!fieldErrors.car_brand}
                      mode={brandMode}
                      onModeChange={(mode) => {
                        setBrandMode(mode);
                        setFieldErrors((prev) => ({
                          ...prev,
                          car_brand: '',
                        }));
                      }}
                    />
                    {fieldErrors.car_brand && (
                      <Box style={{ color: '#FF3A3A', fontSize: '12px', marginTop: '5px' }}>
                        {fieldErrors.car_brand}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <span>{vehicle.car_brand?.name || '-'}</span>
                )}
              </ListItem>

              <ListItem className={styles.listItem}>
                <DynamicParams
                  value={dynamicParams}
                  onChange={handleParamsChange}
                  disabled={!isEditing}
                  error={!!fieldErrors.params}
                />
                {fieldErrors.params && (
                  <Box style={{ color: '#FF3A3A', fontSize: '12px', marginTop: '5px' }}>
                    {fieldErrors.params}
                  </Box>
                )}
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>{isCreateMode ? 'Подтвердите создание' : 'Подтвердите изменения'}</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {Object.keys(fieldErrors).length > 0 ? (
              <Box style={{ color: '#FF3A3A', fontSize: '14px', marginBottom: '10px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Ошибки валидации:</div>
                {Object.values(fieldErrors).map((error, index) => (
                  <div key={index} style={{ marginBottom: '4px' }}>
                    • {error}
                  </div>
                ))}
              </Box>
            ) : isCreateMode ? (
              `Вы уверены, что хотите создать новую запись "${vehicle.name}"?`
            ) : (
              `Вы уверены, что хотите сохранить изменения для модели "${vehicle.name}"?`
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom
            className={styles.modalButtonCancel}
            onClick={() => setShowConfirmDialog(false)}
          >
            Отмена
          </ButtonCustom>
          <ButtonCustom
            className={styles.modalButton}
            onClick={saveVehicle}
            disabled={Object.keys(fieldErrors).length > 0}
          >
            {isCreateMode ? 'Создать' : 'Подтвердить'}
          </ButtonCustom>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='delete-dialog-title'>
          <span>Подтверждение удаления</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить запись "{vehicle.name}"? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom
            className={styles.modalButtonCancel}
            onClick={() => setShowDeleteDialog(false)}
          >
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.redBtn} onClick={deleteVehicle}>
            Удалить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  ) : (
    <Loader />
  );
};
